import { ArrowEnds } from '@/domain/model';

import { DecodeError } from '../codec';
import { CURRENT_SCHEMA_VERSION } from '../schema/v2';

/**
 * Schema migrations.
 *
 * A file records the version it was written with. Loading walks it forward one step at a
 * time until it reaches the version this build understands, then decodes it.
 *
 * ## Why single steps rather than one big upgrade function
 *
 * A file written three versions ago passes through every intermediate transform, so each
 * migration only ever has to know about the version immediately before it. The
 * alternative — one function per old version that jumps straight to current — means
 * every shipped version needs updating whenever the format changes again, and the ones
 * nobody has a test file for quietly rot.
 */

export interface Migration {
  /** The version this migration reads. */
  readonly from: number;
  /** The version it produces. Must be `from + 1`. */
  readonly to: number;
  readonly describe: string;
  migrate(document: Record<string, unknown>): Record<string, unknown>;
}

/**
 * Version 1 → 2: a style gains the ends that carry an arrowhead.
 *
 * Written out rather than left to the decoder's own fallback, and that is the whole point of
 * having a version. The decoder does default a missing field, so an unversioned optional key
 * would have worked and cost nothing — which is exactly the argument that erodes a format one
 * convenience at a time. Version 1 says in the file that it is never edited.
 *
 * A file with no styles at all is left alone rather than given an empty list: a document that
 * never had the key is not a document whose styles were lost.
 */
const arrowsOnStyles: Migration = {
  from: 1,
  to: 2,
  describe: 'thêm `arrows` vào style (v1 → v2)',
  migrate(document) {
    const styles: unknown = document['styles'];
    if (!Array.isArray(styles)) return document;

    return {
      ...document,
      styles: (styles as unknown[]).map((style) =>
        typeof style === 'object' && style !== null
          ? { ...(style as Record<string, unknown>), arrows: ArrowEnds.None }
          : style
      ),
    };
  },
};

/**
 * Every migration, in order.
 *
 * The machinery was written with version 1 and left empty on purpose. This is the first step it
 * has had to take, and it takes it the way the note above describes: one version at a time, so a
 * file written three versions ago passes through every transform and each one only has to know
 * about the version immediately before it.
 */
export const MIGRATIONS: readonly Migration[] = [arrowsOnStyles];

export interface MigrationResult {
  readonly document: Record<string, unknown>;
  readonly applied: readonly string[];
}

/**
 * Brings a raw document up to `targetVersion`.
 *
 * Refuses a file from the future rather than guessing. A newer build may have added
 * fields this one cannot interpret, and opening it anyway would produce a document that
 * silently lost half its content — then autosave would write that loss back over the
 * user's file.
 */
export function migrate(
  raw: unknown,
  migrations: readonly Migration[] = MIGRATIONS,
  targetVersion: number = CURRENT_SCHEMA_VERSION
): MigrationResult {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new DecodeError('expected an object', '');
  }

  let document = { ...(raw as Record<string, unknown>) };
  const rawVersion = document['schemaVersion'];

  if (typeof rawVersion !== 'number' || !Number.isInteger(rawVersion) || rawVersion < 1) {
    throw new DecodeError(`missing or invalid schemaVersion: ${String(rawVersion)}`, '');
  }

  if (rawVersion > targetVersion) {
    throw new DecodeError(
      `file was written by a newer version of EduSketch (schema ${String(rawVersion)}, this build reads up to ${String(targetVersion)})`,
      ''
    );
  }

  const applied: string[] = [];
  let version = rawVersion;

  while (version < targetVersion) {
    const step = migrations.find((candidate) => candidate.from === version);
    if (step === undefined) {
      throw new DecodeError(
        `no migration from schema ${String(version)} to ${String(version + 1)}`,
        ''
      );
    }
    document = { ...step.migrate(document), schemaVersion: step.to };
    applied.push(step.describe);
    version = step.to;
  }

  return { document, applied };
}
